/**
 * Circular colour swatches that delegate to Shopify's own variant picker.
 *
 * The swatches hold no state. A click finds the matching input in the native picker and
 * clicks it, so variant selection, URL, product form, gallery and sticky bar all continue to
 * be driven by the control Shopify already ships — there is one source of truth, presented in
 * KEWELO's visual language rather than as a second row of rectangular buttons.
 *
 * The native colour row stays in the DOM (hidden via `variant-option--delegated`) precisely
 * because it is still doing the work.
 */
const findPickerInput = (value) => {
  const inputs = document.querySelectorAll('variant-picker input[type="radio"], .variant-option input[type="radio"]');
  for (const input of inputs) {
    if (input.value === value) return input;
  }
  return null;
};

const syncSelected = () => {
  for (const swatch of document.querySelectorAll('[data-kw-color]')) {
    const input = findPickerInput(swatch.dataset.kwColor);
    const on = !!input && input.checked;
    swatch.classList.toggle('color-swatch--selected', on);
    swatch.setAttribute('aria-pressed', String(on));

    // The native input is the source of truth for availability as well as for selection. A
    // colour the supplier has not released in the currently chosen frame is disabled there,
    // and a bright clickable circle standing in front of a disabled radio is a control that
    // does nothing — the exact failure this component was built to remove.
    if (input) {
      const blocked = input.disabled;
      swatch.disabled = blocked;
      swatch.classList.toggle('color-swatch--unavailable', blocked);
    }
  }
  const label = document.querySelector('.color-swatches__value');
  const active = document.querySelector('[data-kw-color][aria-pressed="true"]');
  if (label && active) label.textContent = active.dataset.kwColor;
};

document.addEventListener('click', (event) => {
  const swatch = event.target instanceof Element ? event.target.closest('[data-kw-color]') : null;
  if (!swatch) return;
  if (swatch.disabled || swatch.dataset.supplyBlocked === 'true') return;
  const input = findPickerInput(swatch.dataset.kwColor);
  if (!input || input.checked || input.disabled) return;
  input.click();
  // The picker re-renders asynchronously; re-read rather than assume this click won.
  setTimeout(syncSelected, 400);
  setTimeout(syncSelected, 1600);
});

/**
 * The swatches are not the only thing that selects a variant. The final purchase CTA clicks a
 * picker input directly, and so does anything else that drives the native control. Syncing only
 * on our own click left the buy box reading "Color: White" with the white swatch ringed after
 * "Choose Gray" had already switched the variant, the URL, the product form and the gallery —
 * the one part of the page a buyer looks at to confirm the colour was the part that lied.
 *
 * Listening for `change` on the picker itself makes the swatches follow the source of truth
 * whoever moved it. Registered in capture phase so it still runs if a handler stops propagation.
 */
document.addEventListener(
  'change',
  (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== 'radio') return;
    if (!target.closest('variant-picker, .variant-option')) return;
    syncSelected();
    // The picker re-renders after the change; re-read once it has settled.
    setTimeout(syncSelected, 400);
    setTimeout(syncSelected, 1600);
  },
  true
);

// Back/forward restores a different variant without any click at all.
window.addEventListener('popstate', () => setTimeout(syncSelected, 400));

document.addEventListener('DOMContentLoaded', syncSelected);
